const ENV_GA4_ID = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
const ENV_GA4_DISABLE_LOCALHOST =
    (process.env.REACT_APP_GA4_DISABLE_LOCALHOST || "").toLowerCase() ===
    "true";

const UTM_KEY = "adrise_utm_context";

let runtime = {
    ga4MeasurementId: ENV_GA4_ID,
    googleAdsConversionId: "",
    googleAdsLabels: {
        qualified_lead: "",
        complete_registration: "",
        begin_checkout: "",
        purchase: "",
        application_approved: "",
        contact_whatsapp: "",
        contact_telegram: "",
    },
};

let scriptLoaded = false;

function ensureGtagBridge() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
}

function normalizeGoogleAdsId(raw) {
    const v = String(raw || "").trim();
    if (!v) return "";
    return v.startsWith("AW-") ? v : `AW-${v}`;
}

function shouldDisableOnLocalhost() {
    return ENV_GA4_DISABLE_LOCALHOST && window.location.hostname === "localhost";
}

function captureUtmContext(search = "") {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(search || window.location.search);
    const payload = {
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
        utm_term: params.get("utm_term") || "",
        utm_content: params.get("utm_content") || "",
        gclid: params.get("gclid") || "",
        fbclid: params.get("fbclid") || "",
        first_seen_at: new Date().toISOString(),
    };

    const hasAny = Object.entries(payload).some(
        ([key, value]) => key !== "first_seen_at" && Boolean(value),
    );
    if (!hasAny) return;

    const existingRaw = localStorage.getItem(UTM_KEY);
    if (existingRaw) {
        try {
            const existing = JSON.parse(existingRaw);
            localStorage.setItem(
                UTM_KEY,
                JSON.stringify({
                    ...existing,
                    ...Object.fromEntries(
                        Object.entries(payload).filter(([k, v]) =>
                            k === "first_seen_at" ? false : Boolean(v),
                        ),
                    ),
                }),
            );
            return;
        } catch {
            // overwrite below
        }
    }

    localStorage.setItem(UTM_KEY, JSON.stringify(payload));
}

function readUtmContext() {
    if (typeof window === "undefined") return {};
    try {
        const raw = localStorage.getItem(UTM_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function gtagIsReady() {
    return typeof window !== "undefined" && typeof window.gtag === "function";
}

export function configureAnalyticsRuntime(settings = {}) {
    runtime = {
        ga4MeasurementId:
            (settings.ga4_measurement_id || settings.ga4MeasurementId || runtime.ga4MeasurementId || "").trim(),
        googleAdsConversionId: normalizeGoogleAdsId(
            settings.google_ads_conversion_id ||
                settings.googleAdsConversionId ||
                runtime.googleAdsConversionId,
        ),
        googleAdsLabels: {
            qualified_lead:
                settings.google_ads_qualified_lead_label ||
                runtime.googleAdsLabels.qualified_lead ||
                "",
            complete_registration:
                settings.google_ads_complete_registration_label ||
                runtime.googleAdsLabels.complete_registration ||
                "",
            begin_checkout:
                settings.google_ads_begin_checkout_label ||
                runtime.googleAdsLabels.begin_checkout ||
                "",
            purchase:
                settings.google_ads_purchase_label ||
                runtime.googleAdsLabels.purchase ||
                "",
            application_approved:
                settings.google_ads_application_approved_label ||
                runtime.googleAdsLabels.application_approved ||
                "",
            contact_whatsapp:
                settings.google_ads_whatsapp_label ||
                runtime.googleAdsLabels.contact_whatsapp ||
                "",
            contact_telegram:
                settings.google_ads_telegram_label ||
                runtime.googleAdsLabels.contact_telegram ||
                "",
        },
    };
}

export function initGa4() {
    if (typeof window === "undefined") return false;
    if (shouldDisableOnLocalhost()) return false;

    const baseId = runtime.ga4MeasurementId || runtime.googleAdsConversionId;
    if (!baseId) return false;

    ensureGtagBridge();

    if (!scriptLoaded) {
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${baseId}`;
        document.head.appendChild(script);
        scriptLoaded = true;
    }

    window.gtag("js", new Date());
    if (runtime.ga4MeasurementId) {
        window.gtag("config", runtime.ga4MeasurementId, {
            send_page_view: false,
        });
    }
    if (runtime.googleAdsConversionId) {
        window.gtag("config", runtime.googleAdsConversionId);
    }

    return true;
}

export function isGa4Enabled() {
    return Boolean(runtime.ga4MeasurementId) && gtagIsReady();
}

function trackGoogleAdsConversion(type, params = {}) {
    const label = runtime.googleAdsLabels[type] || "";
    const adsId = runtime.googleAdsConversionId || "";
    if (!adsId || !label || !gtagIsReady()) return;

    const payload = {
        send_to: `${adsId}/${label}`,
        value: params.value,
        currency: params.currency || "USD",
        transaction_id: params.transaction_id,
    };
    Object.keys(payload).forEach((k) => {
        if (payload[k] == null || payload[k] === "") delete payload[k];
    });
    window.gtag("event", "conversion", payload);
}

export function trackGa4Event(eventName, params = {}) {
    if (!isGa4Enabled()) return;
    const utm = readUtmContext();
    window.gtag("event", eventName, {
        ...utm,
        ...params,
    });
}

export function trackGa4PageView(pathname, search = "", title = "") {
    if (!isGa4Enabled()) return;
    captureUtmContext(search);
    const page_location = `${window.location.origin}${pathname}${search}`;
    trackGa4Event("page_view", {
        page_location,
        page_title: title || document.title,
    });
}

export function trackGa4Login(method = "email") {
    trackGa4Event("login", { method });
}

export function trackGa4SignUp(method = "email") {
    trackGa4Event("sign_up", { method });
    trackGoogleAdsConversion("qualified_lead", {
        currency: "USD",
        value: 1,
    });
    trackGoogleAdsConversion("complete_registration", {
        currency: "USD",
        value: 1,
    });
}

export function trackContactClick(channel = "whatsapp", params = {}) {
    const eventName = channel === "telegram" ? "contact_telegram" : "contact_whatsapp";
    trackGa4Event("contact", {
        method: channel,
        ...params,
    });
    trackGoogleAdsConversion(eventName, {
        currency: "USD",
        value: params?.value ?? 1,
    });
}

export function trackGa4BeginCheckout({
    plan,
    platform,
    value,
    currency = "USD",
}) {
    trackGa4Event("begin_checkout", {
        currency,
        value,
        items: [
            {
                item_id: plan === "topup" || !plan ? "wallet-topup" : `${plan}-${platform || "unknown"}`,
                item_name: plan === "topup" || !plan ? "Wallet Top-up" : `${platform || "Platform"} ${plan}`,
                item_category: "topup",
                price: value,
                quantity: 1,
            },
        ],
    });

    trackGoogleAdsConversion("begin_checkout", {
        value,
        currency,
    });
}

export function trackGa4Purchase({
    transactionId,
    plan,
    platform,
    value,
    currency = "USD",
}) {
    if (!transactionId) return;
    trackGa4Event("purchase", {
        transaction_id: transactionId,
        value,
        currency,
        items: [
            {
                item_id: plan === "topup" || !plan ? "wallet-topup" : `${plan}-${platform || "unknown"}`,
                item_name: plan === "topup" || !plan ? "Wallet Top-up" : `${platform || "Platform"} ${plan}`,
                item_category: "topup",
                price: value,
                quantity: 1,
            },
        ],
    });

    trackGoogleAdsConversion("purchase", {
        value,
        currency,
        transaction_id: transactionId,
    });
}

export function getGaClientId() {
    if (typeof window === "undefined") return "";
    const raw = document.cookie
        .split("; ")
        .find((r) => r.startsWith("_ga="))
        ?.split("=")[1];
    if (!raw) return "";
    const parts = raw.split(".");
    if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
    return "";
}

export function getUtmContext() {
    if (typeof window === "undefined") return {};
    captureUtmContext(window.location.search);
    return readUtmContext();
}

export function getClickIdsFromContext() {
    const utm = getUtmContext();
    return {
        gclid: utm.gclid || "",
        fbclid: utm.fbclid || "",
    };
}
